import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { BusinessEntity, LegalEntity, Organization } from 'models/index';
import { usePermissions } from 'models/user/permissions';

import { FrameCard, Button, Icon, Table, DropdownItem, DropdownSort, Tooltip, ToastContext, LoaderCard } from '@weezevent/nacre';
import { LoadingCard, InfoTextTooltip } from 'components';

import css from './user.module.css';

export const UserRoles = ({ user, loading, onToggle, handleDelete }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const authenticatedUser = usePermissions();
    const [ordering, setOrdering] = React.useState('alpha');
    const isRoleAdmin = authenticatedUser.canManageRoles();

    const [legalEntities] = LegalEntity.useApiModel({});

    const [businessEntities] = BusinessEntity.useApiModel({});

    const roles = React.useMemo(() => {
        if (!user || loading || !user?.user_roles?.length) {
            return [];
        }
        switch (ordering) {
            case 'alpha':
                return user.user_roles.sort((a, b) => a.role.name?.localeCompare(b?.role.name));
            case '-alpha':
                return user.user_roles.sort((b, a) => a.role.name?.localeCompare(b?.role.name));
            default:
                return user.user_roles;
        }
    }, [user, loading, ordering]);

    const orga_ids = React.useMemo(() => roles.map(r => r.context.value), [roles]);
    const [organizations, loadOrga] = Organization.useApiModel(
        {
            launch: orga_ids?.length > 0,
            cache: false,
            allow_cache: false,
            query: { id__in: orga_ids.join(',') },
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [roles]
    );

    return (
        <FrameCard
            id="roles"
            separator
            subtitle={t('sauron.user.roles')}
            title={
                <span className={css['user-roles-header']}>
                    {t('common.roles')}
                    <Button basic label={t('sauron.user.add-role')} onClick={onToggle} disabled={!isRoleAdmin} icon={<Icon name="plus" size="regular" />} />
                </span>
            }
        >
            <LoadingCard loading={loading} height="20vh">
                <Table useNew className={css['user-roles-table']}>
                    <Table.THead>
                        <Table.Tr isHeader>
                            <th className={css['user-roles-table-medium']}>
                                <DropdownSort
                                    active
                                    name="alpha"
                                    ordering={ordering}
                                    label={t(`common.role`)}
                                    items={[
                                        <DropdownItem.Icons key={1} item={t('common.ordering.alphabetical')} checked={ordering === 'alpha'} onClick={() => setOrdering('alpha')} />,
                                        <DropdownItem.Icons
                                            key={2}
                                            item={t('common.ordering.-alphabetical')}
                                            checked={ordering === '-alpha'}
                                            onClick={() => setOrdering('-alpha')}
                                        />
                                    ]}
                                />
                            </th>
                            <th className={css['user-roles-table-large']}>{t(`common.context`)}</th>
                            {isRoleAdmin && <th className={css['user-roles-table-small']}>{t(`common.actions`)}</th>}
                        </Table.Tr>
                    </Table.THead>
                    <Table.TBody>
                        {!loading &&
                            roles.length > 0 &&
                            roles
                                ?.filter(role => role.context.level <= 3 && !role.context.parents?.some(p => p.value !== '*'))
                                .map((role, i) => (
                                    <RoleItem
                                        key={i}
                                        organization={organizations?.find(o => role.context.value === String(o.id))}
                                        loadOrga={loadOrga}
                                        role={role}
                                        handleDelete={handleDelete}
                                        isRoleAdmin={isRoleAdmin}
                                        legalEntities={legalEntities}
                                        businessEntities={businessEntities}
                                    />
                                ))}
                    </Table.TBody>
                </Table>
            </LoadingCard>
        </FrameCard>
    );
};

UserRoles.propTypes = {
    user: PropTypes.object,
    loading: PropTypes.bool,
    onToggle: PropTypes.func
};

UserRoles.defaultProps = {
    loading: true,
    user: {},
    onToggle: () => {}
};

const RoleItem = ({ role, handleDelete, isRoleAdmin, legalEntities, businessEntities, organization, loadOrga }) => {
    const { t } = useTranslation();

    const contextInfo = () => {
        switch (role.context.level) {
            case 1:
                return (
                    <div className={css['context-table']}>
                        <InfoTextTooltip title={`${legalEntities.find(le => le.uuid === role.context.value).name}`} />
                        <p className={css['context-level']}>{t('common.legal-entity')}</p>
                    </div>
                );
            case 2:
                return (
                    <div className={css['context-table']}>
                        <InfoTextTooltip title={`${businessEntities.find(be => be.uuid === role.context.value).name}`} />
                        <p className={css['context-level']}>{t('common.business-entity')}</p>
                    </div>
                );
            case 3:
                return loadOrga ? (
                    <div className={css['loader-wrapper']}>
                        <LoaderCard active />
                    </div>
                ) : (
                    <div className={css['context-table']}>
                        <InfoTextTooltip title={`O${organization?.id} - ${organization?.name ? organization.name : t('common.unnamed')}`} />
                        <p className={css['context-level']}>{t('common.organization')}</p>
                    </div>
                );
            default:
                return <InfoTextTooltip title="-" />;
        }
    };

    return (
        <Table.Tr>
            <Table.Td>
                <span>{role.role.name}</span>
            </Table.Td>
            <Table.Td>{contextInfo()}</Table.Td>
            {isRoleAdmin && (
                <Table.Td>
                    <Tooltip title={t('sauron.user.delete-role')} triggerOnHover visible>
                        <Icon name="trash" onClick={() => handleDelete(role)} />
                    </Tooltip>
                </Table.Td>
            )}
        </Table.Tr>
    );
};
